import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { LoginService } from '../pages/login/login.service';
import { ToastMessageService } from '../common/services/toast-message.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import * as Sentry from '@sentry/angular';
import { CookieStorageService } from '../common/helpers/cookie-storage.service';

// Shown for any server-side (5xx) failure so raw backend/internal error detail
// is never surfaced to the user. Specific 4xx messages are still shown as-is.
const GENERIC_SERVER_ERROR_MESSAGE = "An error occurred. We're working to fix the issue. Please try again later.";

let isHandlingError = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const cookieService = inject(CookieStorageService);
    const loginService = inject(LoginService);
    const modalService = inject(NzModalService);
    const toastMessageService = inject(ToastMessageService);
    const token = cookieService.get('accessToken');
    const bypassKeywords = ['dev-chisolution-backend-files', 'chisolution-prod-backend-files'];

    if (bypassKeywords.some((keyword) => req.url.includes(keyword))) {
        return next(req);
    }
    const modifiedRequest = token
        ? req.clone({
              setHeaders: {
                  Authorization: `${token}`
              }
          })
        : req;

    return next(modifiedRequest).pipe(
        catchError((error) => {
            // A failed blob download delivers the JSON error body as a Blob, so
            // `error.error.message` is undefined (and reading it off a missing
            // body throws). Compute a safe message for logging.
            const isBlobError = error?.error instanceof Blob;
            const safeErrorMessage = isBlobError ? 'blob error response' : error?.error?.message;

            Sentry.captureException(error, {
                contexts: {
                    http: {
                        url: req.url,
                        method: req.method,
                        status_code: error.status
                    },
                    error_details: {
                        message: safeErrorMessage,
                        error: safeErrorMessage,
                        name: error?.name
                    }
                },
                tags: {
                    httpStatus: error.status,
                    endpoint: req.url
                }
            });

            if (error.status === 401 && !token) {
                return throwError(() => new Error(error.message || 'Unauthorized'));
            }

            // Contract download failures are surfaced by the initiating component
            // with a specific, parsed message — don't also show a generic global
            // toast, and preserve the original error so its blob body stays readable.
            if (req.url.includes('/sign-contract/download-contract/')) {
                return throwError(() => error);
            }

            if (!isHandlingError) {
                isHandlingError = true;
                const errorMessage = error.error?.message || '';
                // Positive match on the backend's deactivation message: a 403 can
                // also be an ordinary authorization rejection (e.g. viewing a
                // contract you are not a party to) — those must NOT trigger the
                // account-deactivated modal and forced logout.
                const isAccountDeactivated = error.status === 403 && errorMessage.toLowerCase().includes('deactivated');
                const isAccountSuspended =
                    error.status === 403 && errorMessage.toLowerCase().includes('suspended');
                if (isAccountDeactivated) {
                    modalService.error({
                        nzTitle: 'Account Deactivated',
                        nzContent: `Your account has been deactivated by the admin. You have been logged out 
                                   and can no longer access your account. 
                                   For further assistance, please reach out to support or your administrator.`,
                        nzWidth: '600px',
                        nzOnOk: () => {
                            loginService.logOut();
                        },
                        nzClassName: 'error-modal'
                    });
                } else if (isAccountSuspended) {
                    modalService.warning({
                        nzTitle: 'Account Suspended',
                        nzContent:
                            'Your subscription has expired & your account is temporarily suspended. Kindly add a payment method or valid coupon to gain access to your account.',
                        nzWidth: '600px',
                        nzOkText: 'OK',
                        nzOnOk: () => {
                            loginService.logOut();
                        },
                        nzClassName: 'error-modal'
                    });
                } else {
                    if (error.status === 401) {
                        loginService.logOut();
                    }
                    let toastErrorMessage = 'Something went wrong';

                    if (error.status >= 500) {
                        // Never surface raw server/internal error detail to the user.
                        toastErrorMessage = GENERIC_SERVER_ERROR_MESSAGE;
                    } else if (error.error) {
                        if (typeof error.error === 'string') {
                            toastErrorMessage = error.error;
                        } else if (error.error.error) {
                            toastErrorMessage = error.error.error;
                        } else if (error.error.message) {
                            toastErrorMessage = error.error.message;
                        }
                    }

                    loginService.isToasterVisible.subscribe((flag) => {
                        if (flag) {
                            toastMessageService.error(toastErrorMessage, { nzDuration: 5000 });
                        }
                    });
                }
                setTimeout(() => (isHandlingError = false), 3000);
            }
            return throwError(() => new Error(error.message || 'Network error'));
        })
    );
};
